import React, { useCallback, useMemo, useState } from "react";
import { router, useGlobalSearchParams } from "expo-router";
import {
  ActivityIndicator,
  Platform,
  StyleSheet,
  useColorScheme,
  View,
} from "react-native";
import {
  BottomSheetHost,
  BottomSheetManagerProvider,
} from "react-native-bottom-sheet-stack";
import { AccessRead, useAccess } from "@hooks/trpc/useAccesses";
import LText from "@components/LText";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import Handle from "../components/common/Handle";

import DetailsLayout from "../layout/Details";

import Buttons from "../components";
import Info from "../components/event/Info";
import Description from "../components/event/Description";
import DataSharingConsent from "../components/music/DataSharingConsent";
import Hero from "../components/details/Hero";
import Title from "../components/details/Title";
import Location from "../components/event/Location";
import Price from "../components/common/Price";
import DateTime from "../components/event/DateTime";
import Artist from "../components/details/Artist";
import ExclusiveSongText from "../components/music/ExclusiveSongText";
import Duration from "../components/music/Duration";
import MembersOnly from "../components/music/MembersOnly";
import { useIsDjSetMusicAccess } from "../components/music/useIsDjSetMusicAccess";
import { useLandingStore } from "store/useLandingStore";
import { getActiveTab } from "@hooks/useActiveTab";
import Collaborators from "../components/common/Collaborators";
import YoureIn from "../components/event/YoureIn";
import Cancelled from "../components/event/Cancelled";
import {
  FOUR_BUTTON_LAYOUT_BUTTON_WIDTH,
  THREE_BUTTON_LAYOUT_BUTTON_WIDTH,
} from "@components/Accesses/AccessDetails/config";
import { AccessDetailsDismiss } from "../types";
import {
  PurchaseFunnelProvider,
  usePurchaseFunnelView,
} from "@components/Checkout/purchaseFunnel";
import {
  useVendorEventEnd,
  VENDOR_EVENT_END_WORDS,
} from "@components/Checkout/vendor/vendorEventEnd";
import { getAccessDetailHeroImage } from "@utils/accesses/access";
import {
  createMusicAccessDetailsModel,
  MUSIC_ACCESS_FLOW_COPY,
} from "@libs/shared-ui";
import {
  headlinePriceLabel,
  MERCH_DOWNLOAD_WORDS,
  merchDeliveryNoteIn,
  merchPriceLabel,
} from "@libs/platform-wire";
import { purchaseHeadline } from "@libs/buy-on-behalf-client/purchase-headline";
import { merchOnSale } from "@components/Accesses/AccessDetails/components/merch/merchOnSale";
import { goodsWords } from "@libs/buy-on-behalf-client/goods";
import { theme } from "@theme";

/** A merch product's UK delivery, or that it is a digital download (which
 *  the store emails), said beside its price as on Link in Bio's merch page
 *  (`merchDeliveryNote`, `MERCH_DOWNLOAD_WORDS`). */
const MerchDelivery = ({ note, download }: { note: string; download: boolean }) => (
  <LBox flexDirection="row" alignItems="center" gap="s1_5">
    <LIcon
      colors={{ color: theme.colors.whiteTranslucent50 }}
      name={download ? "mail" : "merch"}
      size={16}
    />
    <LText variant="Body/B1/Medium" color="white" opacity={0.85}>
      {note}
    </LText>
  </LBox>
);

const AccessDetailLoading = ({
  dismiss,
}: {
  dismiss: AccessDetailsDismiss;
}) => {
  return (
    <DetailsLayout dismiss={dismiss} imageUrl={null}>
      {Platform.OS === "ios" && <Handle />}
      <LBox flex={1} alignItems="center" justifyContent="center">
        <ActivityIndicator size="large" color="white" />
      </LBox>
    </DetailsLayout>
  );
};

const AccessDetail = ({
  access,
  dismiss,
}: {
  access: AccessRead;
  dismiss: AccessDetailsDismiss;
}) => {
  const params = useGlobalSearchParams();
  const storeUnlockCode = useLandingStore((state) => state.unlockCode);
  const paramUnlockCode =
    typeof params.unlockCode === "string" ? params.unlockCode : undefined;
  const unlockCode = storeUnlockCode || paramUnlockCode;
  const isDjSetMusicAccess = useIsDjSetMusicAccess(access);
  const musicModel =
    access.type === "music"
      ? createMusicAccessDetailsModel({
          access,
          unlockCode,
          catalogSongIsSetList: isDjSetMusicAccess,
        })
      : null;

  // `isPaymentPending` mirrors the current param exactly, so derive it.
  const isPaymentPending = params.paymentResult === "success";

  // `recentlyPurchased` latches to true once a successful payment is seen and
  // stays true afterwards. Latch it during render instead of in an effect.
  const [recentlyPurchased, setRecentlyPurchased] = useState(false);
  // Pre-checked per design; the unlock CTA is disabled while unchecked and
  // the final state is recorded server-side with the order.
  const [dataSharingConsent, setDataSharingConsent] = useState(true);
  if (isPaymentPending && !recentlyPurchased) {
    setRecentlyPurchased(true);
  }

  const colorScheme = useColorScheme();
  const isDark = colorScheme === "dark";

  const url =
    access.type === "music"
      ? (musicModel?.heroImageUrl ?? null)
      : (getAccessDetailHeroImage(access) ?? null);

  // Check if access is external:
  // - For music: if extSource is "bandcamp-ingested", it's NOT external (even if extLinks exists)
  // - Otherwise: check if extLinks exists
  const isExternal =
    access.type === "music"
      ? musicModel?.isExternal === true
      : Array.isArray(access.extLinks) && access.extLinks.length > 0;

  // The API names events once (libs/platform-wire eventDisplayTitle).
  const title =
    access.type === "event"
      ? access.title
      : (musicModel?.title ?? access.title);

  const isUnlocked = musicModel?.unlocked === true;
  // Members-only music the fan is not a member for (LIS-1156), decided by
  // the model the Live Link's sheet reads: no price, and the way to its
  // club in place of Unlock.
  const clubArtistName = musicModel?.clubArtistName ?? null;
  const hasTickets =
    access.type === "event" &&
    access.userTickets &&
    // An engine ticket has a code to scan; one bought on a seller's site
    // (`fromSeller`) lives with the seller.
    access.userTickets.some(
      (ticket) => ticket.redeemCode !== null || ticket.fromSeller,
    );

  // A cancelled event stays readable, with nothing left to buy.
  const isCancelled = access.type === "event" && !!access.cancelledAt;

  // A 1-tap event that sells nothing now says why in place of its price,
  // as Link in Bio's page does; its button is Stay updated. Its purchase
  // is the access read's own (LIS-1173).
  const listedEvent =
    access.type === "event" && isExternal && !hasTickets && !isCancelled
      ? access
      : null;
  const eventPurchase = listedEvent?.purchase ?? null;
  const vendorEnded = useVendorEventEnd(eventPurchase, listedEvent);
  // Its price, as Link in Bio's page says it: in the purchase's currency,
  // with the estimate in the fan's own beside it.
  const vendorPrice = eventPurchase ? (purchaseHeadline(eventPurchase)?.price ?? null) : null;

  // A merch product, as Link in Bio's merch page shows it: its price and
  // UK delivery while a size is on sale, and Buy, Share and More. Its
  // purchase says what is on sale (a checkout may have read it again, sold
  // out).
  const merch = access.type === "merch" ? access : null;
  const merchPurchase = merch?.purchase;
  const merchSale = merch ? merchOnSale(merch, merchPurchase) : null;
  const merchDelivery = merchSale?.price
    ? merchSale.download
      ? MERCH_DOWNLOAD_WORDS.label
      : merchDeliveryNoteIn(merchSale.deliveryMinor, merchSale.price.currency)
    : null;

  const isAnonymousTicketPurchaseCompleted =
    access.type === "event" && recentlyPurchased && !hasTickets;

  // Gems are not spent on music (LIS-1038): its price is what its
  // checkout charges.
  const gemEconomicsQuote =
    unlockCode || access.type !== "event" ? null : access.gemEconomicsQuote;
  const hasAppliedGemDiscount =
    !isUnlocked &&
    gemEconomicsQuote?.applied === true &&
    gemEconomicsQuote.appliedDiscountMinorUnits > 0;

  // A merch product the viewer has ordered: My order first, as My Tickets
  // is for an event they hold tickets to, and Buy again beside it.
  const merchOrdered = !!merch?.orders?.length;

  const buttonWidth =
    isAnonymousTicketPurchaseCompleted || (merch && !merchOrdered)
      ? THREE_BUTTON_LAYOUT_BUTTON_WIDTH
      : FOUR_BUTTON_LAYOUT_BUTTON_WIDTH;

  return (
    <>
      <DetailsLayout dismiss={dismiss} imageUrl={url}>
        {({ dismiss }) => (
          <>
            {Platform.OS === "ios" && <Handle />}
            <LBox gap="s3">
              <Hero.Hero
                url={url}
                accessId={access.id}
                showExclusiveRibbon={access.type === "music"}
              />
              <LBox
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Artist.Artist
                  artistName={access.artistName}
                  artistImage={access.artistImage}
                  artistId={access.artistId}
                  dismiss={dismiss}
                />
              </LBox>

              <Title.Title title={title} />

              <LBox gap={hasAppliedGemDiscount ? "s2" : "s1"} mb="s1">
                {access.type === "event" && (
                  <>
                    <DateTime.DateTime
                      startsAtLocal={access.startsAtLocal}
                      endsAtLocal={access.endsAtLocal}
                    />
                    {/* <Location
                      city={access.city}
                      country={access.country}
                      venue={access.venue}
                    /> */}
                  </>
                )}
                {access.type === "music" && (
                  <>
                    <ExclusiveSongText
                      label={
                        musicModel?.exclusiveLabel ??
                        MUSIC_ACCESS_FLOW_COPY.exclusiveSong
                      }
                    />
                    <Duration duration={musicModel?.durationSeconds} />
                  </>
                )}

                {clubArtistName !== null ? (
                  <MembersOnly.Badge />
                ) : !isUnlocked && !isCancelled && !vendorEnded && (
                  <Price.Price
                    price={
                      access.type === "music"
                        ? (musicModel?.effectivePrice ?? access.price)
                        : access.type === "merch"
                          ? (merchSale?.price?.amount ?? null)
                          : (vendorPrice?.amount ?? access.price)
                    }
                    from={
                      access.type === "event"
                        ? (vendorPrice?.from ?? access.priceFrom === true)
                        : merchSale?.price?.from === true
                    }
                    gemEconomicsQuote={gemEconomicsQuote}
                    label={
                      merchSale
                        ? merchPriceLabel(
                            merchSale.price,
                            merchPurchase?.approx,
                            merchSale.taxAdded,
                          )
                        : vendorPrice && vendorPrice.amount > 0
                          ? headlinePriceLabel(
                              vendorPrice,
                              eventPurchase?.approx,
                              eventPurchase?.tax === "added_at_checkout",
                            )
                          : undefined
                    }
                  />
                )}
                {merchDelivery ? (
                  <MerchDelivery
                    note={merchDelivery}
                    download={merchSale?.download === true}
                  />
                ) : null}
              </LBox>
              {merchDelivery && merchSale?.download ? (
                <LText variant="Body/B1/Regular" color="white" opacity={0.85}>
                  {MERCH_DOWNLOAD_WORDS.note}
                </LText>
              ) : null}
              {clubArtistName !== null ? (
                <MembersOnly.Line clubArtistName={clubArtistName} />
              ) : null}
              {merchSale && !merchSale.price ? (
                <LText variant="Body/B1/Regular" color="white" opacity={0.85}>
                  {goodsWords("merch").none}
                </LText>
              ) : null}
              {vendorEnded ? (
                <LText variant="Body/B1/Regular" color="white" opacity={0.85}>
                  {VENDOR_EVENT_END_WORDS[vendorEnded]}
                </LText>
              ) : null}
              {isCancelled ? (
                <Cancelled />
              ) : isAnonymousTicketPurchaseCompleted ? (
                <YoureIn isWaitlist={access.isWaitlist} />
              ) : null}
              <LBox mb="s2">
                <Buttons.common.Container>
                  {access.type === "event" && (
                    <>
                      {/* Tickets first: one bought on the vendor's site
                          belongs to an access that also links there. */}
                      {hasTickets ? (
                        <Buttons.event.MyTickets
                          recentlyPurchased={recentlyPurchased}
                          accessId={access.id}
                        />
                      ) : isCancelled ? null : isExternal ? (
                        <Buttons.event.RegisterExternal
                          accessId={access.id}
                          extLinks={access.extLinks ?? []}
                          event={access}
                        />
                      ) : !isAnonymousTicketPurchaseCompleted ? (
                        <Buttons.event.RegisterInternal
                          access={access}
                          recentlyPurchased={recentlyPurchased}
                        />
                      ) : null}
                      <Buttons.event.Contact width={buttonWidth} />
                    </>
                  )}
                  {access.type === "merch" && (
                    <>
                      {merchOrdered ? (
                        <Buttons.merch.MyOrder
                          accessId={access.id}
                          width={buttonWidth}
                        />
                      ) : null}
                      <Buttons.merch.Buy
                        access={access}
                        width={buttonWidth}
                        ordered={merchOrdered}
                      />
                    </>
                  )}
                  {access.type === "music" && (
                    <>
                      {isExternal ? (
                        <>
                          <Buttons.music.GetSongExternal
                            accessId={access.id}
                            extLinks={access.extLinks ?? []}
                          />
                          <Buttons.music.PreviewExternal
                            accessId={access.id}
                            extLinks={access.extLinks ?? []}
                          />
                        </>
                      ) : (
                        <>
                          {isUnlocked ? (
                            <>
                              <Buttons.music.Unlocked />
                              <Buttons.music.Play
                                song={musicModel?.song}
                                dismiss={dismiss}
                              />
                            </>
                          ) : clubArtistName !== null ? (
                            <>
                              <Buttons.music.JoinFanClub
                                accessId={access.id}
                                clubUrl={access.clubUrl}
                              />
                              <Buttons.music.PreviewInternal
                                song={musicModel?.song}
                              />
                            </>
                          ) : (
                            <>
                              <Buttons.music.Unlock
                                dataSharingConsent={dataSharingConsent}
                                model={musicModel!}
                              />
                              <Buttons.music.PreviewInternal
                                song={musicModel?.song}
                              />
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                  <Buttons.common.Share
                    accessId={access.id}
                    title={title}
                    width={buttonWidth}
                  />
                  <Buttons.common.More
                    width={buttonWidth}
                    options={[
                      // Nothing lists merch beside other merch yet.
                      ...(merch
                        ? []
                        : [
                            {
                              key: "option1",
                              content: (
                                <LBox
                                  flexDirection="row"
                                  alignItems="center"
                                  gap="s1_5"
                                >
                                  <LText
                                    variant="Body/B1/Medium"
                                    color={isDark ? "white" : "black"}
                                  >
                                    See similar{" "}
                                    {access.type === "event"
                                      ? "events"
                                      : "exclusives"}
                                  </LText>
                                  <LIcon
                                    colors={{
                                      color: isDark ? "white" : "black",
                                    }}
                                    name="chevron"
                                    rotate={270}
                                    size={16}
                                  />
                                </LBox>
                              ),
                              onPress: () => {
                                dismiss({
                                  pathname: "/explore/access",
                                });
                              },
                            },
                          ]),
                      {
                        key: "option2",
                        content: (
                          <LBox
                            flexDirection="row"
                            alignItems="center"
                            gap="s1_5"
                          >
                            <LText
                              variant="Body/B1/Medium"
                              color={isDark ? "white" : "black"}
                            >
                              Organiser Profile
                            </LText>
                            <LIcon
                              colors={{ color: isDark ? "white" : "black" }}
                              name="chevron"
                              rotate={270}
                              size={16}
                            />
                          </LBox>
                        ),
                        onPress: () => {
                          dismiss({
                            pathname: `/(app)/(tabs)/${getActiveTab()}/artist/[artistId]`,
                            params: {
                              artistId: access.artistId,
                            },
                          });
                        },
                      },
                    ]}
                  />
                </Buttons.common.Container>
              </LBox>
              {/* The organiser chooses whether Location sits above the
                  line-up; the default keeps the line-up first. */}
              {access.type === "event" && access.locationFirst ? (
                <LBox mb="s2">
                  <Location
                    city={access.city}
                    country={access.country}
                    venue={access.venue}
                    address={access.address}
                  />
                </LBox>
              ) : null}
              {merch ? (
                <LBox mb="s2">
                  <Description text={merch.description} />
                </LBox>
              ) : null}
              <LBox mb="s2">
                <Collaborators
                  collaborators={access.collaborators}
                  lineup={access.lineup}
                  artist={!!merch}
                />
              </LBox>
              {access.type === "event" && (
                <LBox mb="s2">
                  <Description text={access.description} />
                </LBox>
              )}
              {access.type === "event" && !access.locationFirst ? (
                <LBox mb="s2">
                  <Location
                    city={access.city}
                    country={access.country}
                    venue={access.venue}
                    address={access.address}
                  />
                </LBox>
              ) : null}
              {access.type === "event" && <Info text={access.instructions} />}
              {/* The explainer and recommendations rows were replaced by the
                  data-sharing consent row, matching the LIB access sheet
                  (LIS-602). */}
              {access.type === "music" &&
                !isUnlocked &&
                clubArtistName === null &&
                musicModel?.isExternal !== true && (
                  <DataSharingConsent
                    checked={dataSharingConsent}
                    onChange={setDataSharingConsent}
                  />
                )}
            </LBox>
          </>
        )}
      </DetailsLayout>
      {isPaymentPending && access.type === "event" && (
        <LBox
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          top={0}
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
          alignItems="center"
          justifyContent="center"
        >
          <ActivityIndicator size="large" color="white" />
        </LBox>
      )}
    </>
  );
};

const Screen = () => {
  const params = useGlobalSearchParams();
  const { accessId: accessIdParam } = params;

  const accessId = useMemo(() => {
    return Array.isArray(accessIdParam)
      ? accessIdParam[0]
      : accessIdParam || "1";
  }, [accessIdParam]);

  const { data: accessRaw } = useAccess(accessId);
  // The purchase funnel begins with the details (LIS-1110): its sheets,
  // drawn by the host below, are in it too.
  const funnel = usePurchaseFunnelView(accessId, accessRaw);

  const dismiss: AccessDetailsDismiss = useCallback((targetScreen) => {
    if (targetScreen) {
      router.replace(targetScreen);
      return;
    }
    router.back();
  }, []);

  // The Details route is a native modal, which covers the app-wide sheet
  // host; sheets opened from here (Select tickets) need a host of their own
  // inside the modal so they stack over the Details.
  return (
    <BottomSheetManagerProvider id={`access-details-${accessId}`}>
      <PurchaseFunnelProvider value={funnel}>
        <View style={styles.fill}>
          {!accessRaw ? (
            <AccessDetailLoading dismiss={dismiss} />
          ) : (
            <AccessDetail access={accessRaw} dismiss={dismiss} />
          )}
          <View pointerEvents="box-none" style={StyleSheet.absoluteFill}>
            <BottomSheetHost />
          </View>
        </View>
      </PurchaseFunnelProvider>
    </BottomSheetManagerProvider>
  );
};

const styles = StyleSheet.create({ fill: { flex: 1 } });

export default Screen;
